// @ts-check
import { ErrorState, JotType, WidgetState } from "consts";
import {
  getCanonicalUrl,
  getConfigUrl,
  getTopmostPageReferrer,
  getTopmostPageUrl,
  handleError,
  isNullOrUndefOrEmpty,
  normalizeUrl,
  recommendationUtils,
  toObject,
} from "utils";

import { envConfig } from "config";
import fetchJsonp from "fetch-jsonp";
import { jot } from "actions/jot";

/**
 * @typedef {import('../types/api-responses').BootstrapResponse} BootstrapResponse
 * @typedef {import('../types/store').Store} Store
 * @typedef {import('../types/store').WidgetState} WidgetState
 * @typedef {import('../types/store').DeviceData} DeviceData
 */

const { widgetBootstrapEndpoint } = envConfig;

/**
 * Logs poll creation event with content recommendation data
 * @param {string | number} targetId - Target ID for the poll
 * @param {WidgetState} state - Current widget state
 */
const configToJotPollCreated = (targetId, state) => {
  const { urlContext } = toObject(state);

  jot({
    state,
    space: "poll",
    type: JotType.Created,
    data: {
      target: targetId,
      //NOTE: If no content rec link, contentRecUrl value below is undefined, and eventually removed from the
      // data string in the request url created by JSON.stringify inside jot()
      contentRecUrl: recommendationUtils.getRecommendedLinkStorage(urlContext),
    },
  });
};

/**
 * Collects and logs device information for analytics purposes.
 *
 * This function gathers various device characteristics including screen dimensions,
 * touch capabilities, platform information, display properties, and iOS-specific
 * features, then sends this data via the jot logging system.
 *
 * @param {string | number} targetId - The target identifier for the device info collection
 * @param {WidgetState} state - The current application state to be passed to the jot system
 *
 * @description
 * Collected device information includes:
 * - Screen width and height
 * - Maximum touch points supported
 * - Platform information
 * - Device pixel ratio (for retina/high-DPI displays)
 * - Screen orientation type
 * - Hardware concurrency (CPU cores)
 * - Standalone mode detection (PWA/web clip status)
 * - Apple Pay API availability (iOS Safari indicator)
 *
 * The collected data is sent to the analytics system using the jot function
 * with type JotType.Device in the 'poll' space.
 */
const jotDeviceInfo = (targetId, state) => {
  const { width: screenWidth, height: screenHeight, orientation } = window.screen || {};
  // platform is deprecated but still widely supported and useful
  const { platform } = navigator;
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  /** @type {DeviceData} */
  let data = {
    target: targetId,
    sw: screenWidth,
    sh: screenHeight,
    mt: maxTouchPoints,
    plat: platform,
  };

  // Device pixel ratio for retina/high-DPI displays
  if (window.devicePixelRatio) {
    data = { ...data, dpr: window.devicePixelRatio };
  }

  // Orientation and screen orientation
  if (orientation) {
    data = { ...data, orientation: orientation.type, angle: orientation.angle };
  }

  // Hardware concurrency (CPU cores)
  if (navigator.hardwareConcurrency) {
    data = { ...data, cores: navigator.hardwareConcurrency };
  }

  // WebXR capabilities detection
  const hasWebXR = "xr" in navigator;
  data = { ...data, webxr: hasWebXR };

  // Check for iOS-specific features

  // Indicates whether the 'standalone' property exists on the navigator object.
  // This property is available in some browsers (notably iOS Safari) and can be used to detect
  // if the browser supports checking for standalone (PWA) mode via navigator.standalone.
  const hasStandalone = "standalone" in navigator;
  data = { ...data, stnd: hasStandalone, isStnd: hasStandalone && navigator.standalone };

  // WebKit-specific features that might indicate iOS Safari
  const hasApplePayAPI = "ApplePaySession" in window;
  data = { ...data, apay: hasApplePayAPI };

  jot({
    state,
    space: "poll",
    type: JotType.Device,
    data,
  });
};

/**
 * Fetches bootstrap configuration from the API
 *
 * Loads initial widget configuration including user identification,
 * compliance state, and contextual visibility settings.
 *
 * @param {Object} params
 * @param {Store} params.store - Unistore store instance
 * @param {WidgetState} params.state - Current widget state
 * @param {string | number} params.targetId - Target ID to fetch bootstrap for
 * @param {string} params.instanceId - Widget instance ID
 * @returns {Promise<Partial<WidgetState>>} Promise resolving to bootstrap state update, or error state
 */
export const getBootstrap = ({ store, state, targetId, instanceId }) => {
  const topmostPageUrl = getTopmostPageUrl(state.articleUrl || undefined);
  let referrerUrl = getTopmostPageReferrer(state.referrerUrl);
  const urlContext = normalizeUrl(topmostPageUrl);
  referrerUrl = normalizeUrl(referrerUrl);

  const toState = {
    instanceId: instanceId,
    bootstrap: {},
    widgetState: WidgetState.BootstrapLoading,
    urlContext,
    referrerUrl,
  };
  store.setState(toState);
  const { apiTimeoutMS } = envConfig;
  configToJotPollCreated(targetId, store.getState());
  jotDeviceInfo(targetId, store.getState());

  const canonRaw = getCanonicalUrl();
  const canon = canonRaw ? normalizeUrl(canonRaw) : null;
  return fetchJsonp(
    getConfigUrl(
      state,
      targetId,
      instanceId,
      widgetBootstrapEndpoint,
      urlContext,
      topmostPageUrl,
      undefined,
      undefined,
      canon,
    ),
    {
      timeout: apiTimeoutMS,
    },
  )
    .then((res) => res.json())
    .then(
      /** @param {BootstrapResponse} bootstrap */ (bootstrap) => {
        const widgetState = WidgetState.BootstrapLoaded;
        const { hostnameOverride } = bootstrap || {};

        /** @type {Partial<WidgetState>} */
        const updatedBootstrapState = {
          bootstrap,
          urlContext,
          widgetState,
        };

        if (!isNullOrUndefOrEmpty(hostnameOverride)) {
          updatedBootstrapState.hostname = hostnameOverride;
        }

        return updatedBootstrapState;
      },
    )
    .catch((e) => handleError(ErrorState.Bootstrap, e));
};
